function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState } from 'react';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import moment from 'moment';
import { slice, uniqueId } from 'lodash';
import Icon from "../Icon/Icon";
import formatTimeOfDay from "./utils/formatTimeOfDay";
import AllOrOne from "../AllOrOne/AllOrOne";
import { DAYS_OF_THE_WEEK, CLOSED_DAY } from "./constants";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ABBREVIATED_DAYS = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun'
};
const getHours = function (dayOfWeek) {
  let hours = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  let closedText = arguments.length > 2 ? arguments[2] : undefined;
  let startTimeKey = arguments.length > 3 ? arguments[3] : undefined;
  let endTimeKey = arguments.length > 4 ? arguments[4] : undefined;
  const openDay = hours[dayOfWeek];
  let openClose = /*#__PURE__*/_jsx("div", {
    className: "mb-0.5",
    children: closedText
  });
  if (openDay !== undefined) {
    openClose = openDay.map(v => /*#__PURE__*/_jsxs("div", {
      id: uniqueId("".concat(dayOfWeek, "-hours-")),
      children: [formatTimeOfDay(v[startTimeKey]), " - ", formatTimeOfDay(v[endTimeKey])]
    }, uniqueId(dayOfWeek)));
  }
  return openClose;
};
const HoursOfOperationToday = _ref => {
  let {
    className = '',
    hours = {},
    showAll = true,
    showToggle = false,
    shouldStartWeekToday = true,
    abbreviateDays = true,
    closedText = CLOSED_DAY,
    startTimeKey = 'opens',
    endTimeKey = 'closes'
  } = _ref;
  const todayIndex = shouldStartWeekToday ? moment().day() : 1; // today or monday
  const weekFromToday = [...slice(DAYS_OF_THE_WEEK, todayIndex), ...slice(DAYS_OF_THE_WEEK, 0, todayIndex)];
  const [showAllHours, setshowAllHours] = useState(false);
  const hoursToggleClass = isFirst => cn({
    'flex items-start': true,
    'font-heavy-font': isFirst && !showToggle,
    'first:text-action-blue': isFirst && showToggle,
    'group-hover:text-deep-blue': isFirst && showToggle
  });
  const hoursDayLabel = () => cn({
    'mr-4 capitalize inline-block': true,
    'w-auto': !showAllHours && showToggle
  }, abbreviateDays ? 'w-8' : 'w-20');
  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      setshowAllHours(!showAllHours);
    }
  };
  return /*#__PURE__*/_jsx("div", {
    className: "".concat(className, " text-sm text-text-blue group"),
    "data-test-element": "hours-of-operation",
    "aria-label": showAllHours ? 'Organization hours expanded' : 'Organization hours today. Click to expand for hours on other days',
    "aria-live": "polite",
    "aria-expanded": showAllHours,
    children: /*#__PURE__*/_jsx(AllOrOne, {
      showAll: showToggle ? showAll && showAllHours : showAll,
      children: weekFromToday.map((dayOfWeek, index) => /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread({
        className: hoursToggleClass(index === 0),
        onClick: showToggle ? () => setshowAllHours(!showAllHours) : null,
        onKeyDown: handleKeyDown
      }, showToggle ? {
        role: 'button'
      } : null), showToggle ? {
        tabIndex: '0'
      } : null), {}, {
        children: [/*#__PURE__*/_jsx("div", {
          className: hoursDayLabel(),
          children: /*#__PURE__*/_jsx("div", {
            id: uniqueId(dayOfWeek),
            children: abbreviateDays ? ABBREVIATED_DAYS[dayOfWeek] : dayOfWeek
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "inline-block",
          children: getHours(dayOfWeek, hours, closedText, startTimeKey, endTimeKey)
        }), showToggle && showAll && DAYS_OF_THE_WEEK[todayIndex] === dayOfWeek && /*#__PURE__*/_jsx("span", {
          className: "ml-2 my-auto inline-flex",
          children: /*#__PURE__*/_jsx(Icon, {
            color: "action-blue" // should always match link-color (tw: action-blue)
            ,
            icon: "ChevronArrow",
            direction: showAllHours ? 'up' : 'down'
          })
        })]
      }), uniqueId(dayOfWeek)))
    })
  });
};
HoursOfOperationToday.propTypes = {
  /** hours of operation values to display */
  hours: PropTypes.object,
  /** should display all days or only the first one */
  showAll: PropTypes.bool,
  /** should display a separate caret to individually toggle hours */
  showToggle: PropTypes.bool,
  /** should start the days of the week on current day */
  shouldStartWeekToday: PropTypes.bool,
  /** show abbreviated day labels instead of full day (i.e. `Mon` vs `Monday`) */
  abbreviateDays: PropTypes.bool,
  /** text to display when given day has no hours associated */
  closedText: PropTypes.string,
  /** key for start time in hours object (`{ monday: { opens: '09:00', closes: '17:00' } }`) */
  startTimeKey: PropTypes.string,
  /** key for end time in hours object (`{ monday: { opens: '09:00', closes: '17:00' } }`) */
  endTimeKey: PropTypes.string
};
export default HoursOfOperationToday;
//# sourceMappingURL=HoursOfOperationToday.js.map